import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, ShieldCheck, UserCheck, ShieldAlert } from 'lucide-react';
import { safeFetch, extractError } from '../lib/safeFetch';
import ChangePasswordModal from '../components/ChangePasswordModal';
import VersionBadge from '../components/VersionBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [mode, setMode] = useState('admin'); // 'admin' | 'team' | 'gate'
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [slowBusy, setSlowBusy] = useState(false); // >4 s: "server is busy"
  const [reallySlow, setReallySlow] = useState(false); // >12 s: "still trying"
  const [error, setError] = useState('');
  const [expiredUser, setExpiredUser] = useState(null); // {role} when password expired
  const [showCpModal, setShowCpModal] = useState(false);
  const navigate = useNavigate();

  const routeFor = (role) => {
    if (role === 'admin') return '/admin';
    if (role === 'gate') return '/gate';
    return '/staff';
  };

  // If a token is still in localStorage (e.g. user landed here via Android
  // edge-swipe-back gesture), DON'T pretend they're logged out — bounce them
  // straight back into their dashboard. The NativeShell popstate guard also
  // catches this, but mounting Login is the last line of defence.
  useEffect(() => {
    try {
      const tok = localStorage.getItem('token');
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (tok && u && u.role) {
        navigate(routeFor(u.role), { replace: true });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login-tab → backend role mapping (Team tab = "staff" backend role).
  const tabToRole = (m) => (m === 'team' ? 'staff' : m);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    setSlowBusy(false);
    setReallySlow(false);

    // v2.4.9 — friendly "busy, retrying…" banner when the backend takes
    // >4 s to respond (safeFetch is silently retrying underneath).
    // Escalates to "still trying to reach the server…" after 12 s so
    // staff know the app is alive during a morning-burst hang.
    const t1 = setTimeout(() => setSlowBusy(true), 4000);
    const t2 = setTimeout(() => setReallySlow(true), 12000);

    let result;
    try {
      result = await safeFetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, password, requested_role: tabToRole(mode) })
      });
    } finally {
      clearTimeout(t1);
      clearTimeout(t2);
      setSlowBusy(false);
      setReallySlow(false);
    }

    if (result.ok && result.data) {
      const data = result.data;
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      if (data.user.password_expired) {
        // Show inline expiry banner with "Change Now" CTA instead of a blocking alert.
        setExpiredUser(data.user);
        setLoading(false);
        return;
      }

      navigate(routeFor(data.user.role));
    } else {
      setError(extractError(result, 'Login failed'));
    }
    setLoading(false);
  };

  const handleSkipExpiry = () => {
    if (!expiredUser) return;
    navigate(routeFor(expiredUser.role));
  };

  const handlePasswordChanged = () => {
    setShowCpModal(false);
    if (expiredUser) navigate(routeFor(expiredUser.role));
  };

  const tabs = [
    { id: 'admin', label: 'Admin', icon: Shield, activeColor: 'text-blue-600' },
    { id: 'team', label: 'Team', icon: Users, activeColor: 'text-purple-600' },
    { id: 'gate', label: 'Gate', icon: ShieldCheck, activeColor: 'text-amber-600' },
    { id: 'visitor', label: 'Visitor', icon: UserCheck, activeColor: 'text-rose-600' }
  ];

  const handleTabClick = (id) => {
    if (id === 'visitor') {
      navigate('/visitor');
      return;
    }
    setMode(id);
  };

  const cfg = {
    admin: { title: 'Admin Sign In', sub: 'Manage staff, visits & approvals', btn: 'bg-blue-600 hover:bg-blue-700', placeholder: 'Mobile number' },
    team:  { title: 'Team Sign In',  sub: 'Check-in, visits & breaks',       btn: 'bg-purple-600 hover:bg-purple-700', placeholder: 'Mobile number' },
    gate:  { title: 'Gate Sign In',  sub: 'Allow staff in & out',            btn: 'bg-amber-600 hover:bg-amber-700',   placeholder: 'Mobile number' }
  };

  const c = cfg[mode];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_carloo-tex/artifacts/apbouvvy_WhatsApp%20Image%202026-04-14%20at%204.55.05%20PM.jpeg"
            alt="Carloo"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900">Carloo Tex</h1>
          <p className="text-gray-600 mt-2">Field Force Management</p>
          <div className="mt-3 flex justify-center"><VersionBadge /></div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="grid grid-cols-4 gap-1 p-1 bg-gray-100 rounded-xl mb-6" data-testid="login-mode-toggle">
            {tabs.map(t => {
              const Icon = t.icon;
              const active = mode === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleTabClick(t.id)}
                  className={`flex items-center justify-center space-x-1 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                    active ? `bg-white shadow ${t.activeColor}` : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`mode-${t.id}-btn`}
                >
                  <Icon size={14} />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-1">{c.title}</h2>
          <p className="text-sm text-gray-500 mb-6">{c.sub}</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          {/* v2.4.9 — morning-burst friendliness banner */}
          {slowBusy && !error && (
            <div
              className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-4 flex items-start gap-2"
              data-testid="login-slow-notice"
              role="status"
              aria-live="polite"
            >
              <ShieldAlert size={16} className="flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                {reallySlow ? (
                  <>
                    <strong>Still trying to reach the server…</strong>
                    <div className="text-xs text-amber-700 mt-0.5">
                      Peak-hour traffic can slow us down. Keep this screen open, we'll finish signing you in as soon as it responds.
                    </div>
                  </>
                ) : (
                  <>
                    <strong>Server is busy, retrying…</strong>
                    <div className="text-xs text-amber-700 mt-0.5">
                      This usually clears in a few seconds during the morning rush. No need to tap again.
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Mobile Number</label>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={c.placeholder}
                required
                data-testid="login-mobile-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 text-white ${c.btn}`}
              data-testid="login-submit-btn"
            >
              {loading ? 'Logging in...' : `Login as ${c.title.split(' ')[0]}`}
            </button>
          </form>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">© 2026 Carloo Tex. All rights reserved.</p>
      </div>

      {/* Password-expired prompt — replaces blocking alert with a CTA banner. */}
      {expiredUser && !showCpModal && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="pwd-expired-overlay"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-lg bg-amber-100">
                <ShieldAlert size={22} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Your password has expired</h3>
                <p className="text-sm text-gray-600 mt-1">
                  For security, passwords must be changed every 30&nbsp;days.
                  You can update yours now in a few seconds.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={handleSkipExpiry}
                className="px-4 py-2 text-sm rounded-lg text-gray-700 hover:bg-gray-100"
                data-testid="pwd-expired-skip-btn"
              >
                Skip for now
              </button>
              <button
                type="button"
                onClick={() => setShowCpModal(true)}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="pwd-expired-change-btn"
              >
                Change Password Now
              </button>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal
        open={showCpModal}
        forced={!!expiredUser}
        title={expiredUser ? 'Update your expired password' : 'Change Password'}
        subtitle={expiredUser
          ? 'Pick a strong new password — you will be signed in right after.'
          : 'Use a strong password you don’t reuse elsewhere.'}
        onClose={() => setShowCpModal(false)}
        onSuccess={handlePasswordChanged}
      />
    </div>
  );
}
